import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { studentAPI } from '../utils/api';
import '../styles/form.css';

const displayValue = (value) => value || '-';
const today = new Date().toISOString().split('T')[0];

const StudentTransfer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [formData, setFormData] = useState({
    transferDate: today,
    transferRemark: '',
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    fetchStudent();
  }, [id]);

  const fetchStudent = async () => {
    try {
      const response = await studentAPI.getById(id);
      setStudent(response.data.data.student);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || 'Failed to fetch student'}`);
    } finally {
      setPageLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.transferDate) {
      newErrors.transferDate = 'Transfer date is required';
    }
    if (!formData.transferRemark.trim()) {
      newErrors.transferRemark = 'Remark is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await studentAPI.transfer(id, formData);
      setMessage('Student transferred successfully!');
      setTimeout(() => {
        navigate('/students');
      }, 1200);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || 'Failed to transfer student'}`);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <Layout>
        <div className="spinner"></div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout>
        <div className="alert alert-error">Student not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="form-page">
        <div className="form-header">
          <h1>Transfer Student</h1>
          <button className="btn btn-secondary" onClick={() => navigate(`/students/${id}`)}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>

        <div className="card">
          {message && (
            <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
              {message}
            </div>
          )}

          {student.isTransferred ? (
            <div className="alert alert-info">
              This student has already been transferred.
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3>Student Details</h3>

              <div className="profile-content" style={{ marginBottom: '24px' }}>
                <div className="profile-grid">
                  <div className="profile-item">
                    <label>Student Name</label>
                    <p>{student.name}</p>
                  </div>
                  <div className="profile-item">
                    <label>Class</label>
                    <p>{displayValue(student.class)}</p>
                  </div>
                  <div className="profile-item">
                    <label>Father's Name</label>
                    <p>{displayValue(student.fatherName)}</p>
                  </div>
                  <div className="profile-item">
                    <label>Mother's Name</label>
                    <p>{displayValue(student.motherName)}</p>
                  </div>
                  <div className="profile-item">
                    <label>Mobile Number</label>
                    <p>{displayValue(student.mobileNumber)}</p>
                  </div>
                  <div className="profile-item">
                    <label>Date of Admission</label>
                    <p>{student.doa ? new Date(student.doa).toLocaleDateString() : '-'}</p>
                  </div>
                  <div className="profile-item">
                    <label>Category</label>
                    <p>{displayValue(student.category)}</p>
                  </div>
                  <div className="profile-item">
                    <label>Scholar Number</label>
                    <p>{displayValue(student.scholarNumber)}</p>
                  </div>
                </div>
              </div>

              <h3>Transfer Details</h3>

              <div className="form-group">
                <label>Date of Transfer *</label>
                <input
                  type="date"
                  name="transferDate"
                  value={formData.transferDate}
                  onChange={handleChange}
                  max={today}
                />
                {errors.transferDate && <div className="error-message">{errors.transferDate}</div>}
              </div>

              <div className="form-group">
                <label>Remark for Transfer *</label>
                <textarea
                  name="transferRemark"
                  value={formData.transferRemark}
                  onChange={handleChange}
                  placeholder="Enter transfer remark"
                />
                {errors.transferRemark && <div className="error-message">{errors.transferRemark}</div>}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success" disabled={loading}>
                  {loading ? 'Transferring...' : 'Transfer Student'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(`/students/${id}`)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StudentTransfer;
